import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Flag, Check, X, Building2, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import DashboardShell from "@/components/layout/DashboardShell";
import { adminMenu } from "@/config/menus";
import { PageHeader } from "@/components/KpiCard";
import { Button } from "@/components/ui/button";
import { LoadingScreen, EmptyState } from "@/components/common";

const STATUS = { OPEN: "bg-brand-100 text-brand-600", REVIEWED: "bg-emerald-100 text-emerald-700", DISMISSED: "bg-navy-100 text-navy-500" };

export default function AdminReports() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/reports").then((r) => setItems(r.data));
  useEffect(() => { load().catch(() => toast.error("Failed to load")); }, []);
  if (!items) return <DashboardShell menu={adminMenu} title="Profile Reports"><LoadingScreen /></DashboardShell>;

  const setStatus = async (rp, status) => {
    setItems(items.map((x) => (x.id === rp.id ? { ...x, status } : x)));
    try { await api.put(`/admin/reports/${rp.id}`, { status }); toast.success(`Marked ${status.toLowerCase()}`); }
    catch { toast.error("Failed"); load(); }
  };

  return (
    <DashboardShell menu={adminMenu} title="Profile Reports">
      <PageHeader title="Reported Profiles" subtitle="User-submitted reports about incorrect or misleading agency profiles." />
      {items.length === 0 ? (
        <EmptyState icon={Flag} title="No reports yet" hint="Reports submitted from agency profiles will appear here." />
      ) : (
        <div className="space-y-3" data-testid="reports-list">
          {items.map((rp) => (
            <div key={rp.id} className="bg-white border border-navy-200 rounded-xl p-5" data-testid={`report-${rp.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Building2 size={16} className="text-brand" />
                    <Link to={`/agencies/${rp.agency_id}`} className="font-heading font-semibold text-navy-900 hover:text-brand flex items-center gap-1">{rp.agency_name} <ExternalLink size={12} /></Link>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${STATUS[rp.status] || STATUS.OPEN}`}>{rp.status}</span>
                  </div>
                  <p className="mt-2 text-sm font-semibold text-navy-800">{rp.reason}</p>
                  {rp.details && <p className="mt-1 text-sm text-navy-600">{rp.details}</p>}
                  <p className="mt-2 text-xs text-navy-400">
                    {rp.reporter_name || rp.reporter_contact ? `Reported by ${rp.reporter_name || "Anonymous"}${rp.reporter_contact ? ` · ${rp.reporter_contact}` : ""} · ` : "Anonymous · "}
                    {new Date(rp.created_at).toLocaleString("en-IN")}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" variant="outline" className="border-navy-300" onClick={() => setStatus(rp, "REVIEWED")} data-testid={`report-reviewed-${rp.id}`}><Check size={14} className="mr-1" /> Reviewed</Button>
                  <Button size="sm" variant="outline" className="border-navy-300 text-navy-500" onClick={() => setStatus(rp, "DISMISSED")} data-testid={`report-dismiss-${rp.id}`}><X size={14} className="mr-1" /> Dismiss</Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardShell>
  );
}
